import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axiosConn from '../../axiosConn';
import PushSubscribeButton from '../pushSubscribeButton/PushSubscribeButton.jsx';
import PwaInstallHelpSheet from '../installButton/PwaInstallHelpSheet';
import usePwaInstallRecovery from '../installButton/usePwaInstallRecovery';

const STEP_ORDER = [
  { key: 'fullName', label: 'Add your name' },
  { key: 'phoneNumber', label: 'Add your phone number' },
  { key: 'residentialAddress', label: 'Add your address' },
  { key: 'dateOfBirth', label: 'Add your date of birth' },
  { key: 'profilePhoto', label: 'Add a profile photo' },
  { key: 'pwaInstalled', label: 'Add JG LocalOps to your phone' },
  { key: 'pushEnabled', label: 'Turn on LocalOps alerts' },
];

const emptyProfile = {
  fullName: '',
  phoneNumber: '',
  dateOfBirth: '',
  residentialAddress: {
    line1: '',
    line2: '',
    city: '',
    state: '',
    postalCode: '',
  },
  helperCategories: [],
  helperIntroNote: '',
};

const categoryOptions = [
  'Errands',
  'Cleaning',
  'Yard work',
  'Home help',
  'Pet help',
  'Events',
];

const getAuthHeaders = (token) => ({
  headers: {
    Authorization: `Bearer ${token}`,
  },
});

const normalizeRegionSlug = (value) =>
  String(value || 'camden')
    .trim()
    .toLowerCase();

const mergeProfile = (profile = {}) => ({
  ...emptyProfile,
  ...profile,
  residentialAddress: {
    ...emptyProfile.residentialAddress,
    ...(profile.residentialAddress || {}),
  },
  helperCategories: Array.isArray(profile.helperCategories)
    ? profile.helperCategories
    : [],
});

const applyLegacyPrefill = (profile = {}, legacyProfilePrefill = {}) => {
  const nextProfile = mergeProfile(profile);
  const fields = legacyProfilePrefill.fields || {};
  if (!nextProfile.phoneNumber && fields.phoneNumber?.value) {
    nextProfile.phoneNumber = fields.phoneNumber.value;
  }
  if (!nextProfile.dateOfBirth && fields.dateOfBirth?.value) {
    nextProfile.dateOfBirth = fields.dateOfBirth.value;
  }
  return nextProfile;
};

const buildStatusCopy = (readiness = {}) => {
  if (readiness.verifiedHelperApproved) return 'Verified Helper earned.';
  if (readiness.verifiedLocal) {
    return 'You are Verified Local. Finish your required helper profile to become a Verified Helper.';
  }
  if (readiness.requiredProfileComplete) {
    return 'Profile complete. Complete your first verified LocalOps task to become a Verified Helper.';
  }
  return 'Complete your helper profile and finish your first verified LocalOps task to become a Verified Helper.';
};

const getReadinessStep = (readiness = {}, key) => {
  const steps = readiness.steps || {};
  return Object.prototype.hasOwnProperty.call(steps, key)
    ? steps[key] === true
    : undefined;
};

export default function LocalOpsHelperProfileCard({
  cardRef,
  foundingHelperRegionSlug = 'camden',
  isFocused = false,
  onProfileSaved,
  token,
}) {
  const [data, setData] = useState(null);
  const [foundingHelperBenefits, setFoundingHelperBenefits] = useState(null);
  const [form, setForm] = useState(emptyProfile);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedMessage, setSavedMessage] = useState('');
  const [editingEarnedProfile, setEditingEarnedProfile] = useState(false);
  const mountedRef = useRef(false);
  const install = usePwaInstallRecovery('localOpsHelperProfile');
  const normalizedFoundingHelperRegionSlug = useMemo(
    () => normalizeRegionSlug(foundingHelperRegionSlug),
    [foundingHelperRegionSlug],
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchProfile = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const res = await axiosConn.get(
        'local-ops/helper-profile/me',
        getAuthHeaders(token),
      );
      const nextData = res.data?.data || {};
      if (!mountedRef.current) return;
      setData(nextData);
      setForm(
        applyLegacyPrefill(nextData.profile, nextData.legacyProfilePrefill),
      );
      if (nextData.readiness?.verifiedHelperApproved) {
        setEditingEarnedProfile(false);
      }
    } catch (err) {
      if (!mountedRef.current) return;
      setError(
        err?.response?.data?.message ||
          'Could not load LocalOps helper profile readiness.',
      );
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [token]);

  const fetchFoundingHelperBenefits = useCallback(async () => {
    if (!token || !normalizedFoundingHelperRegionSlug) {
      if (mountedRef.current) {
        setFoundingHelperBenefits(null);
      }
      return;
    }
    try {
      const res = await axiosConn.get(
        'local-ops/founding-helper-pass/benefits',
        {
          ...getAuthHeaders(token),
          params: {
            regionSlug: normalizedFoundingHelperRegionSlug,
          },
        },
      );
      if (!mountedRef.current) return;
      setFoundingHelperBenefits(res.data || null);
    } catch (err) {
      if (!mountedRef.current) return;
      setFoundingHelperBenefits(null);
    }
  }, [normalizedFoundingHelperRegionSlug, token]);

  const refreshStatus = useCallback(() => {
    void fetchProfile();
    void fetchFoundingHelperBenefits();
  }, [fetchFoundingHelperBenefits, fetchProfile]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  const readiness = data?.readiness || {};
  const verifiedHelperEarned = readiness.verifiedHelperApproved === true;
  const showEarnedSummary = verifiedHelperEarned && !editingEarnedProfile;
  const foundingHelperActive =
    foundingHelperBenefits?.hasFoundingHelperPass === true &&
    foundingHelperBenefits?.benefits?.founderStatus?.entitled === true;
  const foundingHelperRegion =
    foundingHelperBenefits?.regionDisplayName || 'Camden';
  const foundingHelperDetail = `${foundingHelperRegion} launch benefits active.`;
  const foundingHelperTitle = [
    'First look entitlement, lower helper fee tier entitlement during launch,',
    `and priority access if ${foundingHelperRegion} helper onboarding moves to a waitlist.`,
    'No guaranteed tasks or earnings.',
    'Safety, location, and trust requirements still apply.',
  ].join(' ');
  const legacyProfilePrefill = data?.legacyProfilePrefill || {};
  const showLegacyPrefillNotice =
    legacyProfilePrefill.available === true &&
    legacyProfilePrefill.confirmationRequired === true;
  const statusCopy = data?.message || buildStatusCopy(readiness);
  const helperPushEnabled = getReadinessStep(readiness, 'pushEnabled');
  const missingLabels = useMemo(
    () => new Set((readiness.missingSteps || []).map((step) => step.key)),
    [readiness.missingSteps],
  );

  const updateField = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateAddress = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({
      ...prev,
      residentialAddress: {
        ...prev.residentialAddress,
        [field]: value,
      },
    }));
  };

  const toggleCategory = (category) => {
    setForm((prev) => {
      const current = new Set(prev.helperCategories || []);
      if (current.has(category)) current.delete(category);
      else current.add(category);
      return { ...prev, helperCategories: Array.from(current) };
    });
  };

  const saveProfile = async (event) => {
    event.preventDefault();
    if (!token) return;
    setSaving(true);
    setError('');
    setSavedMessage('');
    try {
      const res = await axiosConn.patch(
        'local-ops/helper-profile/me',
        form,
        {
          ...getAuthHeaders(token),
          params: {
            regionSlug: normalizedFoundingHelperRegionSlug,
          },
        },
      );
      const nextData = res.data?.data || {};
      if (!mountedRef.current) return;
      setData(nextData);
      setForm(
        applyLegacyPrefill(nextData.profile, nextData.legacyProfilePrefill),
      );
      if (nextData.readiness?.verifiedHelperApproved) {
        setEditingEarnedProfile(false);
      }
      setSavedMessage('LocalOps helper profile saved.');
      if (typeof onProfileSaved === 'function') onProfileSaved(nextData);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(
        err?.response?.data?.message || 'Could not save helper profile.',
      );
    } finally {
      if (mountedRef.current) {
        setSaving(false);
      }
    }
  };

  if (!token) return null;

  return (
    <section
      className={`localops-helper-profile-card${
        isFocused ? ' is-focused' : ''
      }`}
      data-testid="localops-helper-profile-card"
      ref={cardRef}
    >
      <div className="localops-helper-profile-card__header">
        <div>
          <div className="profile-completion-eyebrow">JG LocalOps</div>
          <h3>
            {verifiedHelperEarned
              ? 'Verified Helper'
              : 'Complete your LocalOps helper profile'}
          </h3>
          <p>{statusCopy}</p>
        </div>
        <div className="localops-helper-profile-card__badges">
          <span
            className={`localops-helper-profile-card__badge${
              verifiedHelperEarned ? ' is-earned' : ''
            }`}
          >
            {verifiedHelperEarned ? 'Verified Helper' : 'Helper readiness'}
          </span>
          {foundingHelperActive ? (
            <span
              aria-label={`FOUNDING HELPER. ${foundingHelperDetail}`}
              className="localops-helper-profile-card__badge localops-helper-profile-card__badge--founding"
              data-testid="localops-founding-helper-chip"
              title={foundingHelperTitle}
            >
              FOUNDING HELPER
              <small>{foundingHelperDetail}</small>
            </span>
          ) : null}
        </div>
      </div>

      {showEarnedSummary ? (
        <div
          className="localops-helper-profile-earned"
          data-testid="localops-helper-profile-earned-summary"
        >
          <div className="localops-helper-profile-earned__status">
            <strong>Required profile complete</strong>
            <span>Verified Local + helper profile readiness</span>
          </div>
          <div
            aria-label="Verified Helper readiness complete"
            className="localops-helper-profile-earned__chips"
          >
            <span>Verified Local</span>
            <span>Profile complete</span>
            <span>App on phone</span>
            <span>LocalOps alerts on</span>
          </div>
          <p>
            Keep your helper profile current so LocalOps trust operations have
            the right private contact and readiness details.
          </p>
          <div className="localops-helper-profile-actions">
            <button
              className="profile-completion-button"
              onClick={() => setEditingEarnedProfile(true)}
              type="button"
            >
              Edit helper profile
            </button>
            <button
              className="localops-helper-profile-secondary"
              disabled={loading}
              onClick={refreshStatus}
              type="button"
            >
              Refresh status
            </button>
          </div>
          <p className="localops-helper-profile-privacy">
            {data?.privacy ||
              'Your name, phone, address, and date of birth are used for LocalOps profile readiness and trust operations. They are not shown publicly.'}
          </p>
          {loading ? (
            <p className="localops-helper-profile-status">Loading...</p>
          ) : null}
          {savedMessage ? (
            <p className="localops-helper-profile-status is-success">
              {savedMessage}
            </p>
          ) : null}
          {error ? (
            <p className="localops-helper-profile-status is-error">{error}</p>
          ) : null}
        </div>
      ) : (
        <>
          <ul className="localops-helper-profile-steps">
        {STEP_ORDER.map((step) => {
          const done = readiness.steps?.[step.key] === true;
          return (
            <li key={step.key} className={done ? 'is-complete' : ''}>
              <span>{done ? 'Done' : 'Next'}</span>
              <strong>{step.label}</strong>
              {missingLabels.has(step.key) ? <small>Needed</small> : null}
            </li>
          );
        })}
          </ul>

          <form className="localops-helper-profile-form" onSubmit={saveProfile}>
        {showLegacyPrefillNotice ? (
          <div
            className="localops-helper-profile-prefill"
            data-testid="localops-helper-profile-prefill"
          >
            Pre-filled from your existing JG profile. Save to confirm for
            LocalOps Helper readiness.
          </div>
        ) : null}
        <div className="localops-helper-profile-grid">
          <label>
            <span>Full name</span>
            <input
              autoComplete="name"
              onChange={updateField('fullName')}
              placeholder="Your legal name"
              type="text"
              value={form.fullName}
            />
          </label>
          <label>
            <span>Phone number</span>
            <input
              autoComplete="tel"
              inputMode="tel"
              onChange={updateField('phoneNumber')}
              placeholder="Phone for LocalOps coordination"
              type="tel"
              value={form.phoneNumber}
            />
          </label>
          <label>
            <span>Date of birth</span>
            <input
              autoComplete="bday"
              onChange={updateField('dateOfBirth')}
              type="date"
              value={form.dateOfBirth}
            />
          </label>
          <label>
            <span>Address line 1</span>
            <input
              autoComplete="address-line1"
              onChange={updateAddress('line1')}
              placeholder="Street address"
              type="text"
              value={form.residentialAddress.line1}
            />
          </label>
          <label>
            <span>Address line 2</span>
            <input
              autoComplete="address-line2"
              onChange={updateAddress('line2')}
              placeholder="Apartment, suite, optional"
              type="text"
              value={form.residentialAddress.line2}
            />
          </label>
          <label>
            <span>City</span>
            <input
              autoComplete="address-level2"
              onChange={updateAddress('city')}
              type="text"
              value={form.residentialAddress.city}
            />
          </label>
          <label>
            <span>State</span>
            <input
              autoComplete="address-level1"
              onChange={updateAddress('state')}
              type="text"
              value={form.residentialAddress.state}
            />
          </label>
          <label>
            <span>ZIP / postal code</span>
            <input
              autoComplete="postal-code"
              onChange={updateAddress('postalCode')}
              type="text"
              value={form.residentialAddress.postalCode}
            />
          </label>
        </div>

        <fieldset className="localops-helper-profile-categories">
          <legend>Helper categories</legend>
          {categoryOptions.map((category) => (
            <label key={category}>
              <input
                checked={(form.helperCategories || []).includes(category)}
                onChange={() => toggleCategory(category)}
                type="checkbox"
              />
              <span>{category}</span>
            </label>
          ))}
        </fieldset>

        <label className="localops-helper-profile-note">
          <span>Helper intro note</span>
          <textarea
            onChange={updateField('helperIntroNote')}
            placeholder="Short note about the local help you are comfortable doing"
            rows={3}
            value={form.helperIntroNote}
          />
        </label>

        <div className="localops-helper-profile-actions">
          <button
            className="profile-completion-button"
            disabled={saving || loading}
            type="submit"
          >
            {saving ? 'Saving...' : 'Save helper profile'}
          </button>
          <button
            className="localops-helper-profile-secondary"
            disabled={loading}
            onClick={refreshStatus}
            type="button"
          >
            Refresh status
          </button>
          <button
            className="localops-helper-profile-secondary"
            onClick={install.openHelpSheet}
            type="button"
          >
            Add to phone
          </button>
          {process.env.REACT_APP_VAPID_PUBLIC_KEY ? (
            <PushSubscribeButton
              backendPushEnabled={helperPushEnabled}
              jwt={token}
              onSubscribed={refreshStatus}
              product="localops"
              showCompactStatus
              vapidKey={process.env.REACT_APP_VAPID_PUBLIC_KEY}
              variant="compact"
            />
          ) : (
            <p className="localops-helper-profile-status">
              LocalOps alerts are temporarily unavailable.
            </p>
          )}
          {verifiedHelperEarned ? (
            <button
              className="localops-helper-profile-secondary"
              onClick={() => setEditingEarnedProfile(false)}
              type="button"
            >
              Done editing
            </button>
          ) : null}
        </div>

        <p className="localops-helper-profile-privacy">
          {data?.privacy ||
            'Your name, phone, address, and date of birth are used for LocalOps profile readiness and trust operations. They are not shown publicly.'}
        </p>
        {loading ? <p className="localops-helper-profile-status">Loading...</p> : null}
        {savedMessage ? (
          <p className="localops-helper-profile-status is-success">{savedMessage}</p>
        ) : null}
        {error ? (
          <p className="localops-helper-profile-status is-error">{error}</p>
        ) : null}
          </form>
        </>
      )}

      <PwaInstallHelpSheet
        canNativeInstall={install.canNativeInstall}
        nativePromptDismissed={install.nativePromptDismissed}
        onClose={install.closeHelpSheet}
        onInstall={install.promptNativeInstall}
        open={install.isSheetOpenForOwner}
        platform={install.platform}
      />
    </section>
  );
}
